import { useState, type ReactNode } from 'react';
import { api } from '../lib/api.js';
import { useQuery } from '../lib/useQuery.js';
import { Page } from '../app/AppShell.js';
import { BarSeries, RateBar } from '../components/charts.js';
import { Loading, ErrorNote, EmptyState, OnlineDot } from '../components/ui.js';
import type { PopGroupBy } from '../lib/types.js';

const GROUPS: PopGroupBy[] = ['day', 'display', 'layout', 'media'];

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export function ReportsScreen(): ReactNode {
  const [report, setReport] = useState<'proof-of-play' | 'availability'>('proof-of-play');
  const [groupBy, setGroupBy] = useState<PopGroupBy>('day');
  const [from, setFrom] = useState(isoDaysAgo(13));
  const [to, setTo] = useState(isoDaysAgo(0));

  const pop = useQuery(
    () => api.proofOfPlay({ from, to, groupBy }),
    [from, to, groupBy, report],
  );
  const availability = useQuery(() => api.availability({ from, to }), [from, to, report]);

  return (
    <Page title="Reports">
      <div className="row wrap" style={{ marginBottom: 14, gap: 10 }}>
        <select value={report} onChange={(e) => setReport(e.target.value as typeof report)}>
          <option value="proof-of-play">Proof of play</option>
          <option value="availability">Availability</option>
        </select>
        {report === 'proof-of-play' ? (
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as PopGroupBy)}>
            {GROUPS.map((g) => (
              <option key={g} value={g}>
                by {g}
              </option>
            ))}
          </select>
        ) : null}
        <label className="row small" style={{ gap: 4 }}>
          from <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="row small" style={{ gap: 4 }}>
          to <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        {report === 'proof-of-play' ? (
          <a className="btn small" href={api.proofOfPlayCsvUrl({ from, to, groupBy })}>
            Download CSV
          </a>
        ) : null}
      </div>

      {report === 'proof-of-play' ? (
        <>
          {pop.loading && !pop.data ? <Loading what="report" /> : null}
          {pop.error ? <ErrorNote error={pop.error} /> : null}
          {pop.data ? (
            pop.data.rows.length === 0 ? (
              <EmptyState>No proof-of-play in this range.</EmptyState>
            ) : (
              <div className="stack" style={{ gap: 16 }}>
                <div className="card card-pad">
                  <BarSeries
                    data={pop.data.rows.slice(0, 30).map((r) => ({ label: r.label, value: r.plays }))}
                  />
                  <div className="faint small" style={{ marginTop: 4 }}>
                    source: {pop.data.source}
                  </div>
                </div>
                <div className="card">
                  <table className="grid">
                    <thead>
                      <tr>
                        <th>{groupBy}</th>
                        <th>Plays</th>
                        <th>Completed</th>
                        <th>Completion</th>
                        <th>Airtime</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pop.data.rows.map((r) => (
                        <tr key={r.key}>
                          <td>{r.label}</td>
                          <td>{r.plays.toLocaleString()}</td>
                          <td className="muted">{r.playsInFull.toLocaleString()}</td>
                          <td style={{ minWidth: 140 }}>
                            <RateBar value={r.completionRate} />
                          </td>
                          <td className="muted small">{Math.round(r.durationS / 60)} min</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          ) : null}
        </>
      ) : (
        <>
          {availability.loading && !availability.data ? <Loading what="report" /> : null}
          {availability.error ? <ErrorNote error={availability.error} /> : null}
          {availability.data ? (
            <div className="card">
              <table className="grid">
                <thead>
                  <tr>
                    <th />
                    <th>Display</th>
                    <th>Polls seen</th>
                    <th>Availability</th>
                  </tr>
                </thead>
                <tbody>
                  {availability.data.displays.map((d) => (
                    <tr key={d.displayId}>
                      <td>
                        <OnlineDot lastSeenAt={d.lastSeenAt} />
                      </td>
                      <td>{d.name}</td>
                      <td className="muted small">
                        {d.pollsSeen.toLocaleString()} / {d.pollsExpected.toLocaleString()}
                      </td>
                      <td style={{ minWidth: 160 }}>
                        <RateBar value={d.availabilityPct} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      )}
    </Page>
  );
}
